import { useMemo, useState } from 'react'
import { useAtom } from 'jotai'
import { atomWithGlobalStore } from '@highlight/ipc/jotai'
import { getActionTitle } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { useVisibleStagedActions, useStagedActionDerivations, type StagedAction } from '../../hooks/use-staged-actions'

export type ActionFilterType = 'all' | 'staged' | 'completed' | 'archived'

/** Persisted so reopening the drawer lands on the list the user last chose. */
const actionsFilterAtom = atomWithGlobalStore<ActionFilterType>('general', 'sidepanel.outbox.filter', 'staged')

export interface ActionsViewModel {
  isLoading: boolean
  filter: ActionFilterType
  setFilter: (filter: ActionFilterType) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  searchPlaceholder: string
  actions: Array<StagedAction>
  /** Total staged count, for the archive-all confirmation copy. */
  stagedCount: number
}

export function useActionsViewModel(): ActionsViewModel {
  const { allActions, stagedActions, isLoading } = useVisibleStagedActions()
  const { getActionStatus } = useStagedActionDerivations()
  const [filter, setFilter] = useAtom(actionsFilterAtom)
  const [searchQuery, setSearchQuery] = useState('')

  const completedActions = useMemo(() => {
    return (allActions ?? []).filter((action) => {
      const status = getActionStatus(action)
      return status === 'completed' || status === 'pending' || status === 'invoking'
    })
  }, [allActions, getActionStatus])

  const archivedActions = useMemo(() => {
    return (allActions ?? []).filter((action) => action.archivedAt != null)
  }, [allActions])

  const stagedOnlyActions = useMemo(() => {
    return stagedActions.filter((action) => {
      const status = getActionStatus(action)
      return status === 'staged' || status === 'error' || status === 'undo-error'
    })
  }, [stagedActions, getActionStatus])

  const filteredActions = useMemo(() => {
    let items: Array<StagedAction> = []

    if (filter === 'all') {
      items = [...stagedOnlyActions, ...completedActions]
    } else if (filter === 'staged') {
      items = stagedOnlyActions
    } else if (filter === 'completed') {
      items = completedActions
    } else if (filter === 'archived') {
      items = archivedActions
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      items = items.filter((item) => getActionTitle(item).toLowerCase().includes(query))
    }

    // Newest *drafted* first, not newest touched: the server bumps `updatedAt`
    // when an action runs, so sorting on it made a row jump to the top the
    // moment it succeeded — under the user's cursor, mid-list. `createdAt` is
    // fixed for the life of the action, so approving one leaves it where it
    // was. It's also the key Home's "Ready for you" already sorts by, so the
    // head of the queue there and the top of this list agree.
    return [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  }, [stagedOnlyActions, completedActions, archivedActions, filter, searchQuery])

  const searchPlaceholder = useMemo(() => {
    switch (filter) {
      case 'completed':
        return 'Search your completed actions…'
      case 'archived':
        return 'Search your archived actions…'
      case 'staged':
        return 'Search your drafts…'
      default:
        return 'Search your actions…'
    }
  }, [filter])

  return {
    isLoading,
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    searchPlaceholder,
    actions: filteredActions,
    stagedCount: stagedActions.length,
  }
}
